import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import __ from '../utils/translator';
import remToPixels from '../utils/remToPixels';
import JwtStore from '../flux/JwtStore';
import ThronModal from "./ThronModal";
import {
  Term,
  TermInfo,
  TermTitle,
  TermDescription,
  TermTagList,
  TermMediaContainer,
  TermMedia,
} from 'theme-builder/dist/theme-builder';
import thronPublicThumbnail from '../utils/thronPublicThumbnail';
import thronThumbnailResize from '../utils/thronThumbnailResize';
import RenderOnTablet from "./responsive/RenderOnTablet";
import RenderOnMobile from "./responsive/RenderOnMobile";
import NoResults from "./NoResults";
import OutletOnlyLabel from "./OutletOnlyLabel";
import GlossaryStoreActions from "../flux/GlossaryStoreActions";
import trackAnalyticEvent from "../utils/trackAnalyticEvent";
import defaultTrackingOptions from "../utils/defaultTrackingOptions";

function getAssetSrc({ contentId, pKey, type }) {
  return thronThumbnailResize(thronPublicThumbnail(contentId, pKey), {
    width: type === 'VIDEO' ? remToPixels(15) : 0, // we enforce a more square-ish ratio for videos
    height: remToPixels(12),
  });
}

class TermList extends Component {

  state = {
    thronModalOpened: false,
    termAssetClicked: null,
    naturalSlideWidth: this.getNaturalSlideWidth(),
    videoAlreadyStarted: false,
  };

  constructor(props){
    super(props);
    this.onMediaClick = this.onMediaClick.bind(this);
    this.onThronModalCloseButtonClick = this.onThronModalCloseButtonClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.getNaturalSlideWidth = this.getNaturalSlideWidth.bind(this);
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  onThronModalCloseButtonClick(){
    this.setState({ thronModalOpened: false, termAssetClicked: null, videoAlreadyStarted: false});
  }

  onMediaClick(asset) {
    this.setState({
      thronModalOpened: true,
      termAssetClicked: asset,
    });
  }

  handleResize(e){
    this.setState({
      naturalSlideWidth: e.target.outerWidth
    });
  }

  getNaturalSlideWidth(){
    return window.screen.width;
  }

  retrieveRelatedTerm = (terms, assetId) => {
    let term = terms.find(term =>
      term.assets?.some(asset => asset.contentId === assetId)
    );
    return term;
  }

  handleVideoPlay = () => {
    if(this.state.termAssetClicked.type === "VIDEO" && !this.state.videoAlreadyStarted){
      let term = this.retrieveRelatedTerm(this.props.terms, this.state.termAssetClicked.contentId);
      trackAnalyticEvent('video_player',
        {
          ...defaultTrackingOptions(),
          event: 'start_video',
          video_id: this.state.termAssetClicked.contentId,
          video_title: term?.name,
        });
      this.setState({videoAlreadyStarted: true})
    }
  };

  handleVideoComplete = () => {
    if(this.state.termAssetClicked.type === "VIDEO"){
      let term = this.retrieveRelatedTerm(this.props.terms, this.state.termAssetClicked.contentId);
      trackAnalyticEvent('video_player',
        {
          ...defaultTrackingOptions(),
          event: 'complete_video',
          video_id: this.state.termAssetClicked.contentId,
          video_title: term?.name,
        });
    }
  };

  render(){
    if(this.props.terms.length === 0){
      return (
        <NoResults
          message={__('No Glossary terms found')}
          tip={__('Try to change your filters')}
        />
      )
    }
    return (
      <Fragment>
        <RenderOnMobile>
          {this.props.terms.map((term, index, array) => (
            <TermInfo
              key={term.id}
              style={{
                position: 'relative',
                margin: '0 1rem',
                padding: '1.825rem 0',
                borderBottom: index < (array.length - 1) ? '.0625rem solid rgba(0, 0, 0, .25)' : '0',
              }}
              onShow={() => {
                GlossaryStoreActions.viewGlossaryTerm(term.id)
              }}
            >
              {JwtStore.hasGlobalVisibility() && term.visibleOnlyForOutlets ? <OutletOnlyLabelContainerMobile/> : null}

              <TermTitle
                awsCredentials={JwtStore.getAwsCredentials()}
                term={term}
                style={{
                  display: 'flex',
                  flex: 'initial',
                  alignItems: 'center',
                }}
              />

              <TermMediaContainer>
                {term.assets.map(asset => (
                  <TermMedia
                    key={asset.id}
                    asset={asset}
                    type={asset.type}
                    src={getAssetSrc(asset)}
                    onClick={() => this.onMediaClick(asset)}/>
                ))}
              </TermMediaContainer>

              <TermDescription>
                {term.description}
              </TermDescription>

              <TermTagList tags={term.tags}/>
            </TermInfo>
          ))}
        </RenderOnMobile>

        <RenderOnTablet>
          <div style={{padding: '1rem 0',}}>
            {this.props.terms.map(term => (
              <TermStyled key={term.id}>
                {JwtStore.hasGlobalVisibility() && term.visibleOnlyForOutlets ? <OutletOnlyLabelContainer/> : null}
                <TermInfo
                  onShow={() => {
                    GlossaryStoreActions.viewGlossaryTerm(term.id)
                  }}
                >
                  <TermTitle
                    awsCredentials={JwtStore.getAwsCredentials()}
                    term={term}/>

                  <TermDescription>
                    {term.description}
                  </TermDescription>

                  <TermTagList tags={term.tags}/>
                </TermInfo>

                <TermMediaContainer>
                  {term.assets.map(asset => (
                    <TermMedia
                      key={asset.id}
                      asset={asset}
                      type={asset.type}
                      src={getAssetSrc(asset)}
                      onClick={() => this.onMediaClick(asset)}/>
                  ))}
                </TermMediaContainer>
              </TermStyled>
            ))}
          </div>
        </RenderOnTablet>

        {this.state.thronModalOpened && (
          <ThronModal asset={this.state.termAssetClicked} onCloseButtonClick={this.onThronModalCloseButtonClick} onPlay={this.handleVideoPlay} onComplete={this.handleVideoComplete}/>
        )}
      </Fragment>
    )
  }
}

export default TermList;

const TermStyled = styled(Term)`
  max-width: 58rem;
  margin: 0 auto;
  padding: 3rem 1rem 1.125rem;
  position: relative;
`;

const OutletOnlyLabelContainerMobile = styled(OutletOnlyLabel)`
  position: relative;
  display: inline-block;
  right: auto;
  left: 0;
  margin-bottom: 0.25rem;
`

const OutletOnlyLabelContainer = styled(OutletOnlyLabel)`
  right: auto;
  left: 1rem;
  top: 1rem;
`
